import { Link } from 'react-router-dom';
import PageShell, { PageTitle } from '../components/PageShell';
import CallButton from '../components/CallButton';
import HelpBox from '../components/HelpBox';
import ArticleList from '../components/ArticleList';
import FaqAccordion from '../components/FaqAccordion';
import { SUPPORT_ARTICLES } from '../constants/articles';
import { FAQ_GROUPS } from '../constants/content';
import { BRAND_NAME, SUPPORT_PHONE_LABEL, SUPPORT_PHONE_TEL } from '../constants/site';
import { openZohoChat } from '../utils/openZohoChat';

const SERVICES = [
  ['Getting Started', 'Downloading the program, running the installer, entering your serial number, and fixing activation messages.'],
  ['Syncing & Cloud', 'Stalled FamilySync, duplicated people after a sync, broken tree links, and TreeVault questions.'],
  ['Crashes & Error Messages', 'Programs that will not open, freeze mid-task, run slowly, or show confusing warnings.'],
  ['Lost Pictures', 'Reconnecting photos and scans after a move, a restore, or a change of drive letter.'],
  ['New Editions & New Computers', 'Moving to FTM 2024 or to a replacement PC or Mac with nothing left behind.'],
  ['Backups & Rescue', 'Setting up a backup routine you will actually use, and restoring trees when things go wrong.'],
  ['Online Accounts', 'Ancestry and FamilySearch logins, hints, downloading trees, and account changes.'],
  ['Learning the Program', 'Charts, reports, books, sources, relationships, and color coding explained simply.'],
];

const VERSIONS = [
  ['Family Tree Maker 2024', 'Windows & Mac', 'Complete support'],
  ['Family Tree Maker 2019', 'Windows & Mac', 'Complete support'],
  ['Family Tree Maker 2017', 'Windows & Mac', 'Complete support'],
  ['Family Tree Maker 2014 / 2014.1', 'Windows & Mac', 'Support and upgrade advice'],
  ['Family Tree Maker 2012 and older', 'Windows & Mac', 'Data recovery and upgrade advice'],
];

const ERROR_TABLE = [
  ['Program closes right after opening', 'Damaged tree file or outdated build', 'Open a new test tree, update FTM, then compact or restore your tree'],
  ['Serial number not accepted', 'Serial from a different edition, or a typing slip', 'Re-enter it carefully and make sure it matches the edition you installed'],
  ['Broken-link icons on photos', 'Files moved or renamed outside the program', 'Run Find Missing Media from the Media workspace'],
  ['Sync seems to run forever', 'Slow connection or large uploads', 'Use a wired or stronger connection and leave the program open'],
  ['Everything feels slow', 'A large tree that has not been maintained', 'Back up, then run Compact File'],
];

export default function SupportPage() {
  return (
    <PageShell>
      <PageTitle
        title="Family Tree Maker Support Center"
        lead="Self-help guides and one-to-one technical support for every edition of Family Tree Maker."
        crumbs={[{ label: 'Support' }]}
      />

      <p>
        This is the {BRAND_NAME} Support Center. Browse the topics below if you like to fix things
        yourself, or contact us and let a technician do the heavy lifting. Before you call, jot down your
        Family Tree Maker edition, whether you use Windows or a Mac, and the exact wording of any error.
        Those three details save a lot of time.
      </p>

      <CallButton />

      <section className="entry-section">
        <h2>Areas We Cover</h2>
        <div className="service-grid">
          {SERVICES.map(([title, text]) => (
            <div key={title} className="service">
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="entry-section">
        <h2>Supported Editions</h2>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Edition</th>
                <th scope="col">Runs on</th>
                <th scope="col">What we offer</th>
              </tr>
            </thead>
            <tbody>
              {VERSIONS.map(([version, platform, level]) => (
                <tr key={version}>
                  <td>{version}</td>
                  <td>{platform}</td>
                  <td>{level}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>From First Contact to Fixed</h2>
        <div className="steps-grid">
          <div className="service">
            <h3>Get in touch</h3>
            <p>
              Dial <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_LABEL}</a> or start a chat and tell us what
              is wrong.
            </p>
          </div>
          <div className="service">
            <h3>Find the cause</h3>
            <p>We ask about your edition, computer, and anything that changed recently.</p>
          </div>
          <div className="service">
            <h3>Fix it</h3>
            <p>We guide you through the solution or, if you agree, connect to your screen.</p>
          </div>
          <div className="service">
            <h3>Prevent a repeat</h3>
            <p>We help you set up backups so a future problem never costs you data.</p>
          </div>
        </div>
        <p className="text-center">
          <button type="button" className="btn-outline" onClick={openZohoChat}>
            Chat With a Technician
          </button>
        </p>
      </section>

      <section className="entry-section">
        <h2>Decoding Common Error Messages</h2>
        <div className="table-wrap">
          <table className="data-table data-table--left">
            <thead>
              <tr>
                <th scope="col">What you notice</th>
                <th scope="col">Probable cause</th>
                <th scope="col">Where to start</th>
              </tr>
            </thead>
            <tbody>
              {ERROR_TABLE.map(([seen, cause, fix]) => (
                <tr key={seen}>
                  <td><strong>{seen}</strong></td>
                  <td>{cause}</td>
                  <td>{fix}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Five-Minute Fixes to Try Before Calling</h2>
        <ol>
          <li>Make a backup from the File menu so you have a safe copy.</li>
          <li>Restart the computer, then reopen Family Tree Maker.</li>
          <li>Check the Help menu for a program update and install it.</li>
          <li>Install any Windows or macOS updates that are waiting.</li>
          <li>Temporarily pause antivirus or firewall software and try again.</li>
          <li>Run Compact File if the tree is slow or unstable.</li>
          <li>Create a tiny test tree. If that works fine, the issue is with your main tree file, not the program.</li>
        </ol>
      </section>

      <section className="entry-section">
        <h2>When to Stop and Ask for Help</h2>
        <p>
          Some situations are best handled with a second pair of eyes, because a wrong click can make
          things harder to undo:
        </p>
        <ul>
          <li>Your only copy of the tree will not open and you have no recent backup.</li>
          <li>A sync has created large numbers of duplicate people.</li>
          <li>Hundreds of photos are missing after a computer change.</li>
          <li>You are about to cancel an Ancestry account or delete an online tree.</li>
          <li>You are moving between Windows and Mac for the first time.</li>
        </ul>
      </section>

      <HelpBox
        title="Nothing Working? Let Us Take a Look"
        text="Skip the trial and error. A technician will start with a safe backup and fix the problem alongside you."
      />

      <section className="entry-section">
        <h2>Popular Self-Help Articles</h2>
        <ArticleList articles={SUPPORT_ARTICLES} />
        <p className="text-center">
          <Link to="/blogs" className="btn-outline">
            See the full library
          </Link>
        </p>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Questions About Our Service</h2>
        <FaqAccordion items={FAQ_GROUPS.find((g) => g.id === 'help-desk').items} />
      </section>

      <CallButton />
    </PageShell>
  );
}
